/* eslint-disable complexity */
/* eslint-disable max-len */
import { RegionIdEnum } from '@search/filter-enums/enums/Region';
import { getDistrictMapperByRegion, getCityMapperByRegion, getMetroMapperByRegion } from './mappers';
import { mskRayonMapper } from './mappers/regions/msk/mskRayonMapper';
import { detectRegion } from './mappers/regions/detectRegion';
import { mskMetroBranchMapper } from './mappers/regions/msk';

export function seoPlaceUrlParams({
    geos,
    regionIdSuggested = RegionIdEnum.MSK,
    isCanonical,
    isServer,
    newBuildingType,
    urlMapper
}: {
    urlMapper?(src: string): string | undefined;
    geos: {
        kind?: string | null;
        id: number;
        title?: string | null;
        locative?: string | null;
        translit?: string | null;
        locality?: {
            id: number;
            locative: string;
        } | null;
        narrowRegion?: {
            id: number;
        };
    }[];
    regionIdSuggested?: number;
    isCanonical?: boolean;
    isServer?: boolean;
    newBuildingType?: string | undefined;
}) {
    const count = {
        districts: 0,
        streets: 0,
        stations: 0
    };

    for (const geo of geos) {
        if (geo.kind === 'STREET') count.streets++;
        else if (geo.kind === 'METRO') count.stations++;
        else count.districts++;
    }

    const segmentSlugs = {
        geoSlug1: undefined as string | undefined,
        geoSlug2: undefined as string | undefined,
        okrug: undefined as string | undefined,
        rayon: undefined as string | undefined,
        metro: undefined as string | undefined
    };

    const queryParams = {
        districtIds: [] as number[],
        addresses: [] as number[],
        metroIds: [] as number[]
    };

    const segmentGeos = [] as typeof geos;

    const districtMapper = getDistrictMapperByRegion(regionIdSuggested);
    const cityMapper = getCityMapperByRegion(regionIdSuggested);
    const metroMapper = getMetroMapperByRegion(regionIdSuggested);
    const isZhk = newBuildingType === 'zhk';

    for (const geo of geos) {
        if (isZhk && isServer) continue;
        if (geo.id === regionIdSuggested) continue;
        if (mskMetroBranchMapper.hasType(geo.id)) continue;

        if (geo.kind === 'STREET' || geo.kind === 'HOUSE') {
            if (geo.translit && count.districts === 0 && count.streets <= 1 && count.stations === 0 && ! isZhk) {
                segmentSlugs.geoSlug1 = geo.translit;
                segmentGeos.push(geo);
                continue;
            }
            queryParams.addresses.push(geo.id);
            continue;
        }

        if (geo.kind === 'METRO') {
            const metroSlug = count.stations === 1 ? metroMapper?.seoFromTypeOptional(geo.id) : undefined;
            const metroOriginSlug = isCanonical ? metroSlug?.match(/(.*?)-?\d+$/)?.[1] : undefined;

            if (metroOriginSlug && metroMapper?.hasSeo(metroOriginSlug) && ! isZhk) {
                segmentSlugs.metro = metroOriginSlug;
                segmentGeos.push(geo);
                continue;
            }

            if (metroSlug && ! isZhk) {
                segmentSlugs.metro = metroSlug;
                segmentGeos.push(geo);
                continue;
            }

            queryParams.metroIds.push(geo.id);
            continue;
        }

        let cityLoc = geo.id === 390 ? 'zelenograd' : cityMapper?.seoFromTypeOptional(geo.id);

        let okrugLoc = ! cityLoc && districtMapper?.name === 'mskDistrictMapper' ?
            districtMapper.seoFromTypeOptional(geo.id) :
            undefined;

        if (okrugLoc && ! cityLoc) {
            cityLoc = urlMapper?.(okrugLoc + '-okrug');
            if (cityLoc) okrugLoc = undefined;
        }

        if (cityLoc && count.districts === 1 && count.streets === 0 && count.stations <= 1 && ! isZhk) {
            segmentSlugs.geoSlug1 = cityLoc;
            segmentGeos.push(geo);
            continue;
        }

        const rayonLoc = districtMapper?.name === 'spbDistrictMapper' ?
            districtMapper.seoFromTypeOptional(geo.id) :
            mskRayonMapper.seoFromTypeOptional(geo.id);

        if (rayonLoc && count.districts === 1 && count.streets === 0 && count.stations === 0 && ! isZhk) {
            segmentSlugs.rayon = rayonLoc;
            segmentGeos.push(geo);
            continue;
        }

        if (okrugLoc && count.districts === 1 && count.streets === 0 && count.stations === 0 && ! isZhk) {
            segmentSlugs.okrug = okrugLoc;

            segmentGeos.push(geo);
            continue;
        }

        if (geo.translit && count.districts === 1 && count.streets === 0 && count.stations === 0 && ! isZhk) {
            segmentSlugs.geoSlug1 = urlMapper?.(geo.translit) ?? geo.translit;
            segmentGeos.push(geo);
            continue;
        }

        queryParams.districtIds.push(geo.id);
    }

    const regionId = detectRegion({ ...segmentSlugs, geos, region: regionIdSuggested, isCanonical, isServer }) ?? regionIdSuggested;

    return { segmentSlugs, segmentGeos, queryParams, regionId };
}
